import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Typography,
    Chip,
    Alert,
    AlertTitle,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from "@mui/material"
import { styled } from "@mui/material/styles"
import * as actions from "./actions"
import { toastError, toastSuccess } from '../../utils';
import moment from "moment"
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SelectCmp from "react-select"
import { reasonList, ticketFilterOptions } from '../../constants';
import * as commonActions from "../common/actions"
import { useHistory } from "react-router-dom"
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SEO from '../../components/SEO';

const PageContainer = styled(Box)({
    minHeight: 'calc(100vh - 64px)',
    background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
    padding: '32px 24px',
    position: 'relative',
});

const PageHeader = styled(Box)({
    marginBottom: '32px',
    '& h1': {
        fontSize: '2rem',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '8px',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    '& p': {
        fontSize: '1rem',
        color: '#5f6368',
        fontFamily: "'DM Sans', sans-serif",
    },
});

const StyledButton = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
    },
});

const StyledTableContainer = styled(TableContainer)({
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    backgroundColor: 'white',
    overflow: 'hidden',
    '& .MuiTable-root': {
        fontFamily: "'DM Sans', sans-serif",
    },
    '& .MuiTableHead-root': {
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        '& .MuiTableCell-head': {
            color: 'white',
            fontWeight: 700,
            fontSize: '0.9375rem',
            fontFamily: "'DM Sans', sans-serif",
            padding: '16px',
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f0f7ff',
                transform: 'scale(1.005)',
            },
        },
    },
    '& .MuiTableCell-body': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.875rem',
        color: '#202124',
        padding: '16px',
        borderBottom: '1px solid #f0f0f0',
    },
});

const StatusChip = styled(Chip)(({ statustype }) => ({
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    fontFamily: "'DM Sans', sans-serif",
    ...(statustype === 'resolved' ? {
        backgroundColor: '#4caf50',
        color: '#ffffff',
    } : {
        backgroundColor: '#ff9800',
        color: '#ffffff',
    }),
}));

const CountCard = styled(Box)({
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    borderRadius: '16px',
    padding: '16px 28px',
    boxShadow: '0 4px 16px rgba(26, 115, 232, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(26, 115, 232, 0.35)',
    },
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '580px',
        width: '100%',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        backgroundColor: '#fafafa',
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
    },
}));

const StyledDialogTitle = styled(DialogTitle)({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '1.25rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const StyledDialogContent = styled(DialogContent)({
    fontFamily: "'DM Sans', sans-serif",
    padding: '20px',
    backgroundColor: '#fafafa',
});

const StyledDialogActions = styled(DialogActions)({
    padding: '12px 20px',
    gap: '12px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
});

const CreateButton = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    fontSize: '0.9375rem',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
    },
});

const CancelButton = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'white',
    color: '#5f6368',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    fontSize: '0.9375rem',
    border: '2px solid #e0e0e0',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: '#f5f5f5',
        borderColor: '#1a73e8',
        color: '#1a73e8',
        transform: 'translateY(-2px)',
    },
});

const selectStyles = {
    control: (base, state) => ({
        ...base,
        border: `1px solid ${state.isFocused ? "#1a73e8" : "rgba(0, 0, 0, 0.12)"} `,
        boxShadow: "none",
        background: "white",
        borderRadius: "12px",
        minHeight: "48px",
        height: "48px",
        fontFamily: "'DM Sans', sans-serif",
    }),
    placeholder: (defaultStyles) => {
        return {
            ...defaultStyles,
            color: "#5f6368",
            fontFamily: "'DM Sans', sans-serif",
        };
    },
    singleValue: (defaultStyles) => {
        return {
            ...defaultStyles,
            color: "#202124",
            fontFamily: "'DM Sans', sans-serif",
        };
    },
    option: (defaultStyles, state) => {
        return {
            ...defaultStyles,
            backgroundColor: state.isSelected ? "#1a73e8" : state.isFocused ? "#e3f2fd" : "white",
            color: state.isSelected ? "white" : "#202124",
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
        };
    },
    menu: (defaultStyles) => {
        return {
            ...defaultStyles,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            fontFamily: "'DM Sans', sans-serif",
        };
    },
};

const inititalTicket = {
    reason: '',
    reply: '',
    attachement: []
}

export default function Tickets(props) {

    const history = useHistory();

    const [ticketsList, setTicketsList] = useState();
    const [ticket, setTicket] = useState({ ...inititalTicket })
    const [open, setOpen] = useState(false)
    const [files, setFiles] = useState(null)
    const [selectedTicket, setSelectedTicket] = useState({ label: "All", value: "all" })
    const [count, setCount] = useState(0)

    useEffect(() => {
        getAllTickets()
    }, [])

    const getAllTickets = () => {
        props.setLoader(true)
        actions.getTicketListForUser().then(res => {
            props.setLoader(false)
            setTicketsList(res.body)
            setCount(res.total || 0)
        }).catch(err => {
            props.setLoader(false)
            toastError(err.message)
        })
    }

    const onFileSelect = (filesObj) => {
        const files = Object.values(filesObj)

        if (Array.isArray(files) && files.length > 5) {
            return toastError('Max 5 attachments are allowed.')
        }
        let allFiles = []
        if (Array.isArray(files)) {
            for (let i = 0; i < files.length; i++) {
                if (!isValidExtension(files[i])) {
                    return toastError('Invalid file format selected.')
                }
                if (files[i].size > 2097152) {
                    return toastError('File more than 2 MB is not allowed.')
                } else {
                    allFiles.push(files[i])
                }
            }
        }
        setFiles(allFiles)
    }

    const handleClose = () => {
        setOpen(false)
        setTicket({ ...inititalTicket })
        setFiles(null)
    }

    const isValidExtension = (file) => {
        let lastDot = file.name.lastIndexOf(".")
        let ext = file.name.substring(lastDot + 1)
        if (ext === 'jpg' ||
            ext === 'txt' ||
            ext === 'pdf' ||
            ext === 'png' ||
            ext === 'doc' ||
            ext === 'jpeg' ||
            ext === 'excel'
        ) {
            return true
        }
        return false
    }

    const onSubmitTicket = async () => {
        if (!ticket.reason) {
            return toastError('Please select a reason.')
        }
        if (!ticket.reply) {
            return toastError('Reason details is Required.')
        }
        const filesArray = []
        props.setLoader(true)
        if (Array.isArray(files) && files.length) {
            for (let i = 0; i < files.length; i++) {
                await commonActions.upload(files[i]).then(res => {
                    filesArray.push(res.body.filename)
                }).catch(err => {
                    props.setLoader(false)
                    return toastError(err.message)
                })
            }
        }

        actions.createTicket({ ...ticket, attachement: filesArray.join(",") }).then(res => {
            props.setLoader(false)
            setOpen(false)
            setTicket({ ...inititalTicket })
            toastSuccess(res.message)
            getAllTickets()
        }).catch(err => {
            props.setLoader(false)
            toastError(err.message)
        })

    }

    let showTicketsList = ticketsList
    if (selectedTicket.value !== "all") {
        showTicketsList = ticketsList.filter(item => item.status === selectedTicket.value)
    }

    return (
        <PageContainer>
            <SEO
                title="Support Tickets"
                description="Track and manage all your support tickets and requests for assistance."
            />
            <Fade in timeout={800}>
                <Box>
                    {/* Page Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                        <PageHeader sx={{ mb: 0 }}>
                            <Typography variant="h1">
                                <ConfirmationNumberIcon sx={{ fontSize: 40, color: '#1a73e8' }} />
                                Support Tickets
                            </Typography>
                            <Typography>
                                Track and manage your support tickets
                            </Typography>
                        </PageHeader>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '200px' }}>
                            <CountCard>
                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Total Tickets
                                </Typography>
                                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>
                                    {count}
                                </Typography>
                            </CountCard>
                        </Box>
                    </Box>

                    {/* Filter and New Ticket Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ minWidth: '250px', maxWidth: '300px' }}>
                            <Typography sx={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#202124',
                                mb: 1,
                            }}>
                                Filter by Status
                            </Typography>
                            <SelectCmp
                                styles={selectStyles}
                                value={selectedTicket}
                                options={ticketFilterOptions}
                                onChange={e => setSelectedTicket(e)}
                                placeholder="Filter tickets..."
                            />
                        </Box>
                        <StyledButton onClick={() => setOpen(true)}>
                            <AddIcon sx={{ fontSize: 20, mr: 1 }} />
                            New Ticket
                        </StyledButton>
                    </Box>

                    {/* Info Alert */}
                    <Alert
                        severity="warning"
                        icon={<WarningAmberIcon />}
                        sx={{
                            mb: 3,
                            borderRadius: '12px',
                            '& .MuiAlert-message': {
                                fontFamily: "'DM Sans', sans-serif",
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 600 }}>Important Notice</AlertTitle>
                        Our support team is experiencing high volume. Response times typically range from 24 hours to 7 business days. We appreciate your patience and will address your concern as soon as possible.
                    </Alert>

                    {/* Tickets Table */}
                    <StyledTableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No.</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>Created On</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    showTicketsList && showTicketsList.length ? showTicketsList.map((item, index) => {
                                        return <TableRow key={index} onClick={() => history.push(`/tickets/${item.ticket_id}`)}>
                                            <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#202124' }}>
                                                {item.reason}
                                            </TableCell>
                                            <TableCell sx={{ color: '#5f6368' }}>
                                                {moment(item.date).format('DD/MM/YYYY')}
                                            </TableCell>
                                            <TableCell>
                                                <StatusChip
                                                    statustype={item.status === 0 ? 'open' : 'resolved'}
                                                    label={item.status === 0 ? 'OPEN' : 'RESOLVED'}
                                                    icon={item.status === 0 ? <PendingIcon /> : <CheckCircleIcon />}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    }) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                                <ConfirmationNumberIcon sx={{ fontSize: 60, color: '#e0e0e0', mb: 2, display: 'block', margin: '0 auto 16px' }} />
                                                <Typography sx={{ color: '#5f6368', fontWeight: 600, fontSize: '1rem' }}>
                                                    No tickets found
                                                </Typography>
                                                <Typography sx={{ color: '#9e9e9e', fontSize: '0.875rem', mt: 1 }}>
                                                    Create your first ticket to get support
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                </Box>
            </Fade>

            <StyledDialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                disableScrollLock
            >
                <StyledDialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ConfirmationNumberIcon sx={{ fontSize: 24 }} />
                        Create New Ticket
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </StyledDialogTitle>

                <StyledDialogContent>
                    <Grid container spacing={2.5}>
                        {/* Reason Field */}
                        <Grid item xs={12}>
                            <Typography sx={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#202124',
                                mb: 0.75,
                                mt: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}>
                                Reason <span style={{ color: '#d32f2f' }}>*</span>
                            </Typography>
                            <SelectCmp
                                styles={selectStyles}
                                options={reasonList}
                                onChange={e => setTicket({ ...ticket, reason: e.value })}
                                placeholder="Select a reason for your ticket..."
                            />
                        </Grid>

                        {/* Description Field */}
                        <Grid item xs={12}>
                            <Typography sx={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#202124',
                                mb: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}>
                                Description <span style={{ color: '#d32f2f' }}>*</span>
                            </Typography>
                            <Box
                                component="textarea"
                                value={ticket.reply}
                                onChange={(e) => setTicket({ ...ticket, reply: e.target.value })}
                                placeholder="Please provide detailed information about your issue..."
                                sx={{
                                    width: '100%',
                                    minHeight: '140px',
                                    padding: '12px 16px',
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.9375rem',
                                    borderRadius: '12px',
                                    border: '2px solid #e0e0e0',
                                    backgroundColor: '#f8f9fa',
                                    color: '#202124',
                                    resize: 'vertical',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: '#1a73e8',
                                        backgroundColor: '#ffffff',
                                    },
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: '#1a73e8',
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 0 0 3px rgba(26, 115, 232, 0.1)',
                                    },
                                    '&::placeholder': {
                                        color: '#9e9e9e',
                                    }
                                }}
                            />
                        </Grid>

                        {/* File Upload Field */}
                        <Grid item xs={12}>
                            <Typography sx={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#202124',
                                mb: 0.75
                            }}>
                                Attachments (Optional)
                            </Typography>
                            <Box
                                component="label"
                                htmlFor="upload-reason"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '24px 16px',
                                    borderRadius: '12px',
                                    border: '2px dashed #bdbdbd',
                                    backgroundColor: '#f8f9fa',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: '#1a73e8',
                                        backgroundColor: '#e3f2fd',
                                    }
                                }}
                            >
                                <AddIcon sx={{ fontSize: 32, color: '#1a73e8', mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, color: '#202124', fontSize: '0.875rem', mb: 0.25 }}>
                                    {files && Array.isArray(files) && files.length ? `${files.length} file(s) selected` : 'Choose files'}
                                </Typography>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: '#5f6368', textAlign: 'center' }}>
                                    Max 5 files, 2MB each
                                </Typography>
                            </Box>
                            <input
                                type="file"
                                id="upload-reason"
                                onChange={e => onFileSelect(e.target.files)}
                                multiple
                                style={{ display: 'none' }}
                            />

                            {/* File Info */}
                            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: '#f5f5f5' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.6875rem', color: '#5f6368' }}>
                                    <strong>Supported:</strong> JPG, TXT, PDF, PNG, DOC, EXCEL (2MB max per file)
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Selected Files List */}
                        {files && Array.isArray(files) && files.length ? (
                            <Grid item xs={12}>
                                <Box sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #bbdefb'
                                }}>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, color: '#202124', mb: 1, fontSize: '0.8125rem' }}>
                                        Selected Files:
                                    </Typography>
                                    {files.map((item, index) => (
                                        <Box key={index} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.5,
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontSize: '0.8125rem',
                                            color: '#202124'
                                        }}>
                                            <Box sx={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                backgroundColor: '#1a73e8',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.6875rem',
                                                fontWeight: 600,
                                                flexShrink: 0
                                            }}>
                                                {index + 1}
                                            </Box>
                                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8125rem', wordBreak: 'break-word' }}>
                                                {item.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        ) : null}
                    </Grid>
                </StyledDialogContent>

                <StyledDialogActions>
                    <CancelButton onClick={handleClose}>
                        Cancel
                    </CancelButton>
                    <CreateButton onClick={onSubmitTicket}>
                        <AddIcon sx={{ fontSize: 20, mr: 0.5 }} />
                        Create Ticket
                    </CreateButton>
                </StyledDialogActions>
            </StyledDialog>
        </PageContainer>
    )
}
